/**
 * Post-its and assigned-template API endpoints.
 */

import apiClient from './api-client';
import {
  PostIt,
  CreatePostItDto,
  UpdatePostItDto,
  PostItStatus,
  AssignedPostIt,
  CreateAssignedPostItDto,
  BulkAssignedPostItDto,
  BulkAssignedPostItResult,
  TrackingOccurrence,
  TrackingUsersResponse,
  PostItFilters,
  PostItListResponse,
  AssignedPostItFilters,
  AllowedTargetRole,
} from '@/types/post-it.types';

/** `GET /post-its` — paginated personal post-its. */
export const getPostIts = async (filters?: PostItFilters): Promise<PostItListResponse> => {
  const params = new URLSearchParams();

  if (filters?.archived !== undefined) {
    params.append('archived', filters.archived.toString());
  }
  if (filters?.priority !== undefined) {
    params.append('priority', filters.priority.toString());
  }
  if (filters?.thematic) {
    params.append('thematic', filters.thematic);
  }
  if (filters?.status) {
    params.append('status', filters.status);
  }
  if (filters?.search) {
    params.append('search', filters.search);
  }
  if (filters?.page) {
    params.append('page', filters.page.toString());
  }
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters?.dateFrom) {
    params.append('dateFrom', filters.dateFrom);
  }
  if (filters?.dateTo) {
    params.append('dateTo', filters.dateTo);
  }

  const response = await apiClient.get<PostItListResponse>(`/post-its?${params.toString()}`);
  return response.data;
};

/** `POST /post-its` */
export const createPostIt = async (data: CreatePostItDto): Promise<PostIt> => {
  const response = await apiClient.post<PostIt>('/post-its', data);
  return response.data;
};

/** `PATCH /post-its/:id` */
export const updatePostIt = async (id: string, data: UpdatePostItDto): Promise<PostIt> => {
  const response = await apiClient.patch<PostIt>(`/post-its/${id}`, data);
  return response.data;
};

/** `DELETE /post-its/:id` — soft-delete. */
export const deletePostIt = async (id: string): Promise<void> => {
  await apiClient.delete(`/post-its/${id}`);
};

/** `PATCH /post-its/:id/archive` */
export const toggleArchive = async (id: string): Promise<PostIt> => {
  const response = await apiClient.patch<PostIt>(`/post-its/${id}/archive`);
  return response.data;
};

/** `PATCH /post-its/:id/priority` */
export const togglePriority = async (id: string): Promise<PostIt> => {
  const response = await apiClient.patch<PostIt>(`/post-its/${id}/priority`);
  return response.data;
};

/** `PATCH /post-its/:id/status` */
export const updateStatus = async (id: string, status: PostItStatus): Promise<PostIt> => {
  const response = await apiClient.patch<PostIt>(`/post-its/${id}/status`, { status });
  return response.data;
};

/** `POST /post-its/:id/share` */
export const sharePostIt = async (
  id: string,
  userIds: string[],
  permission: 'read' | 'write',
): Promise<{ message: string; sharesCount: number }> => {
  const response = await apiClient.post(`/post-its/${id}/share`, { userIds, permission });
  return response.data;
};

/** `POST /post-its/:id/share/role` */
export const sharePostItByRole = async (
  id: string,
  roleCode: string,
  permission: 'read' | 'write',
): Promise<{ message: string; sharesCount: number }> => {
  const response = await apiClient.post(`/post-its/${id}/share/role`, { roleCode, permission });
  return response.data;
};

/** `DELETE /post-its/:id/share/:userId` */
export const removeShare = async (id: string, userId: string): Promise<void> => {
  await apiClient.delete(`/post-its/${id}/share/${userId}`);
};

/** `GET /post-its/:id/shares` */
export const getShares = async (id: string) => {
  const response = await apiClient.get(`/post-its/${id}/shares`);
  return response.data;
};

/** `DELETE /post-its/:id/hard` */
export const hardDeletePostIt = async (id: string): Promise<void> => {
  await apiClient.delete(`/post-its/${id}/hard`);
};

/** `DELETE /post-its/trash/empty` */
export const emptyTrash = async (): Promise<{ count: number }> => {
  const response = await apiClient.delete<{ count: number }>('/post-its/trash/empty');
  return response.data;
};

/** `GET /post-its/assigned` — assigned templates. */
export const getAssignedPostIts = async (
  filters?: AssignedPostItFilters,
): Promise<AssignedPostIt[]> => {
  const params = new URLSearchParams();
  if (filters?.filter && filters.filter !== 'all') params.append('filter', filters.filter);
  if (filters?.startDateFrom) params.append('startDateFrom', filters.startDateFrom);
  if (filters?.startDateTo) params.append('startDateTo', filters.startDateTo);
  if (filters?.endDateFrom) params.append('endDateFrom', filters.endDateFrom);
  if (filters?.endDateTo) params.append('endDateTo', filters.endDateTo);
  if (filters?.mode) params.append('mode', filters.mode);
  filters?.quarterlyPeriods?.forEach((p) => params.append('quarterlyPeriods', String(p)));
  const qs = params.toString();
  const url = qs ? `/post-its/assigned?${qs}` : '/post-its/assigned';
  const response = await apiClient.get<AssignedPostIt[]>(url);
  return response.data;
};

/**
 * `GET /post-its/assigned/allowed-target-roles` — hierarchy-scoped target roles.
 * Pass `appMode`:
 * - remote → roleGroup admins|volunteers AND mode remote|hybrid
 * - in_person → roleGroup admins|volunteers AND mode in_person|hybrid
 */
export const getAllowedTargetRoles = async (
  appMode?: 'in_person' | 'remote',
): Promise<AllowedTargetRole[]> => {
  const params = new URLSearchParams();
  if (appMode) params.set('mode', appMode);
  const qs = params.toString();
  const response = await apiClient.get<AllowedTargetRole[]>(
    qs
      ? `/post-its/assigned/allowed-target-roles?${qs}`
      : '/post-its/assigned/allowed-target-roles',
  );
  return response.data;
};

/** `PATCH /post-its/assigned/config-access/:roleId` */
export const updateConfigAccess = async (
  roleId: string,
  visible: boolean,
): Promise<{ success: boolean }> => {
  const response = await apiClient.patch<{ success: boolean }>(
    `/post-its/assigned/config-access/${roleId}`,
    { visible },
  );
  return response.data;
};

/** `POST /post-its/assigned` */
export const createAssignedPostIt = async (
  data: CreateAssignedPostItDto,
): Promise<AssignedPostIt> => {
  const response = await apiClient.post<AssignedPostIt>('/post-its/assigned', data);
  return response.data;
};

/** `PATCH /post-its/assigned/:id` */
export const updateAssignedPostIt = async (
  id: string,
  data: Partial<CreateAssignedPostItDto>,
): Promise<AssignedPostIt> => {
  const response = await apiClient.patch<AssignedPostIt>(`/post-its/assigned/${id}`, data);
  return response.data;
};

/** `DELETE /post-its/assigned-templates/:id` */
export const deleteAssignedPostIt = async (id: string): Promise<void> => {
  await apiClient.delete(`/post-its/assigned-templates/${id}`);
};

/** `POST /post-its/assigned/bulk` */
export const bulkUpdateAssignedPostIts = async (
  data: BulkAssignedPostItDto,
): Promise<BulkAssignedPostItResult> => {
  const response = await apiClient.post<BulkAssignedPostItResult>('/post-its/assigned/bulk', data);
  return response.data;
};

/** `DELETE /post-its/assigned-templates/bulk` */
export const bulkDeleteAssignedTemplates = async (ids: string[]): Promise<{ deleted: number }> => {
  const response = await apiClient.delete<{ deleted: number }>(
    '/post-its/assigned-templates/bulk',
    {
      data: { ids },
    },
  );
  return response.data;
};

/** `PATCH /post-its/assigned-templates/bulk` */
export const bulkUpdateAssignedTemplates = async (data: {
  ids: string[];
  data: { isPriority?: boolean };
}): Promise<{ updated: number }> => {
  const response = await apiClient.patch<{ updated: number }>(
    '/post-its/assigned-templates/bulk',
    data,
  );
  return response.data;
};

/** `GET /post-its/assigned-templates/:id/next-occurrences` */
export const getNextOccurrences = async (
  id: string,
  count: number = 12,
): Promise<Array<{ key: string; label: string; date: string }>> => {
  const response = await apiClient.get<Array<{ key: string; label: string; date: string }>>(
    `/post-its/assigned-templates/${id}/next-occurrences?count=${count}`,
  );
  return response.data;
};

/** `GET /post-its/assigned-templates/:templateId/tracking/occurrences` */
export const getTrackingOccurrences = async (templateId: string): Promise<TrackingOccurrence[]> => {
  const response = await apiClient.get<TrackingOccurrence[]>(
    `/post-its/assigned-templates/${templateId}/tracking/occurrences`,
  );
  return response.data;
};

/** `GET /post-its/assigned-templates/:templateId/tracking/users` */
export const getTrackingUsers = async (
  templateId: string,
  params: {
    periodKey: string;
    status?: PostItStatus;
    search?: string;
    page?: number;
    pageSize?: number;
  },
): Promise<TrackingUsersResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append('periodKey', params.periodKey);
  if (params.status) queryParams.append('status', params.status);
  if (params.search) queryParams.append('search', params.search);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

  const url = `/post-its/assigned-templates/${templateId}/tracking/users?${queryParams.toString()}`;
  const response = await apiClient.get<TrackingUsersResponse>(url);
  return response.data;
};

/** `PATCH /post-its/assigned-templates/:templateId/tracking/force-complete` */
export const forceCompleteForUser = async (
  templateId: string,
  userId: string,
  periodKey: string,
): Promise<void> => {
  await apiClient.patch(`/post-its/assigned-templates/${templateId}/tracking/force-complete`, {
    userId,
    periodKey,
  });
};
